import { openDB, type DBSchema } from "idb";
import { Note, NoteInput } from "@ca/shared";
import { api, HttpError } from "./api.ts";
import { uuidv7 } from "./queue.ts";
export interface NoteDraft {
  key: string;
  uid: string;
  id: string;
  writer: string;
  body: NoteInput;
  dirty: boolean;
  version: string;
  request: { key: string; body: NoteInput } | null;
  error: string | null;
  blocked: boolean;
  updatedAt: number;
}
interface Store extends DBSchema {
  drafts: { key: string; value: NoteDraft };
  pages: { key: string; value: { key: string; note: Note } };
}
export class NoteStore {
  private db;
  private flight: Promise<void> | null = null;
  constructor(name = "ca-notes", privateSend: typeof api = api) {
    this.send = privateSend;
    this.db = openDB<Store>(name, 1, {
      upgrade(db) {
        db.createObjectStore("drafts", { keyPath: "key" });
        db.createObjectStore("pages", { keyPath: "key" });
      },
    });
  }
  private send: typeof api;
  async list(uid: string) {
    return (await (await this.db).getAll("drafts")).filter((x) => x.uid === uid);
  }
  async get(key: string) {
    return (await this.db).get("drafts", key);
  }
  async cache(uid: string, note: Note) {
    await (await this.db).put("pages", { key: `${uid}:${note.id}`, note });
  }
  async cached(uid: string, id: string) {
    return (await (await this.db).get("pages", `${uid}:${id}`))?.note;
  }
  async cachedList(uid: string) {
    return (await (await this.db).getAll("pages"))
      .filter((x) => x.key.startsWith(`${uid}:`))
      .map((x) => x.note);
  }
  async retry(key: string) {
    const db = await this.db,
      old = await db.get("drafts", key);
    if (old)
      await db.put("drafts", {
        ...old,
        blocked: false,
        error: null,
        request:
          old.request &&
          parseInt(old.request.key.replaceAll("-", "").slice(0, 12), 16) < Date.now() - 7 * 86400000
            ? null
            : old.request,
      });
  }
  async remove(key: string) {
    await (await this.db).delete("drafts", key);
  }
  async forget(uid: string, id: string) {
    const db = await this.db;
    await db.delete("pages", `${uid}:${id}`);
    for (const draft of await this.list(uid))
      if (draft.id === id) await db.delete("drafts", draft.key);
  }
  async put(uid: string, id: string, writer: string, raw: NoteInput) {
    const key = `${uid}:${id}:${writer}`,
      db = await this.db,
      tx = db.transaction("drafts", "readwrite"),
      old = await tx.store.get(key);
    // Retain a persisted retry snapshot even when the user types a newer version during a failed request.
    const draft: NoteDraft = {
      key,
      uid,
      id,
      writer,
      body: NoteInput.parse({ ...raw, rev: Math.max(raw.rev, old?.body.rev ?? 0) }),
      dirty: true,
      version: uuidv7(),
      request: old?.request ?? null,
      blocked: old?.blocked ?? false,
      error: old?.error ?? null,
      updatedAt: Date.now(),
    };
    await tx.store.put(draft);
    await tx.done;
    return draft;
  }
  async drain(uid: string) {
    if (this.flight) {
      await this.flight;
      return;
    }
    const run = async () => {
      const db = await this.db;
      for (const listed of await this.list(uid)) {
        const prepare = db.transaction("drafts", "readwrite");
        let draft = await prepare.store.get(listed.key);
        if (!draft?.dirty || draft.blocked) {
          await prepare.done;
          continue;
        }
        if (!draft.request) {
          draft = { ...draft, request: { key: uuidv7(), body: draft.body } };
          await prepare.store.put(draft);
        }
        await prepare.done;
        const request = draft.request;
        if (!request) continue;
        try {
          const note = Note.parse(
            await this.send(`/v1/me/notes/${draft.id}`, {
              method: "PUT",
              body: request.body,
              key: request.key,
              accountId: uid,
            }),
          );
          await this.cache(uid, note);
          const finish = db.transaction("drafts", "readwrite");
          const current = await finish.store.get(draft.key);
          if (current) {
            const changed =
              current.version !== draft.version ||
              JSON.stringify(current.body) !== JSON.stringify(request.body);
            await finish.store.put({
              ...current,
              body: changed
                ? { ...current.body, rev: note.rev, context: note.context }
                : toInput(note),
              dirty: changed,
              request: null,
              blocked: false,
              error: null,
            });
          }
          await finish.done;
        } catch (error) {
          const fail = db.transaction("drafts", "readwrite");
          const current = await fail.store.get(draft.key);
          if (current)
            await fail.store.put({
              ...current,
              blocked: error instanceof HttpError && error.status < 500 && error.status !== 429,
              error: error instanceof Error ? error.message : "Chưa đồng bộ được.",
            });
          await fail.done;
          if (!(error instanceof HttpError) || error.status >= 500 || error.status === 401) break;
        }
      }
    };
    this.flight = (
      typeof navigator !== "undefined" && "locks" in navigator
        ? navigator.locks.request(`ca-notes:${uid}`, run)
        : run()
    ).finally(() => {
      this.flight = null;
    });
    await this.flight;
  }
}
export function toInput(note: Note): NoteInput {
  const { rev, method, title, doc, paper, color, font, tags, context, attemptId } = note;
  return { rev, method, title, doc, paper, color, font, tags, context, attemptId };
}
export function sanitizeLocalNote(html: string): string {
  const doc = document.createElement("template");
  doc.innerHTML = html;
  const tags = new Set([
    "P",
    "DIV",
    "SPAN",
    "BR",
    "B",
    "STRONG",
    "I",
    "EM",
    "U",
    "S",
    "MARK",
    "SUB",
    "SUP",
    "H2",
    "H3",
    "UL",
    "OL",
    "LI",
    "TABLE",
    "THEAD",
    "TBODY",
    "TR",
    "TD",
    "TH",
    "BLOCKQUOTE",
  ]);
  for (const element of [...doc.content.querySelectorAll("*")]) {
    if (!tags.has(element.tagName)) {
      if (
        ["SCRIPT", "STYLE", "IFRAME", "OBJECT", "SVG", "MATH", "TEMPLATE"].includes(element.tagName)
      )
        element.remove();
      else element.replaceWith(...element.childNodes);
      continue;
    }
    for (const attr of [...element.attributes]) {
      if (attr.name === "style") {
        const styles = element instanceof HTMLElement ? element.style : null;
        const safe = styles
          ? ["color", "background-color", "font-weight", "text-align"]
              .map((p) => {
                const v = styles.getPropertyValue(p);
                return v && !/url|expression|import|javascript/i.test(v) ? `${p}:${v}` : "";
              })
              .filter(Boolean)
              .join(";")
          : "";
        element.setAttribute("style", safe);
      } else if (
        !(element.tagName === "TD" || element.tagName === "TH") ||
        !["colspan", "rowspan"].includes(attr.name)
      )
        element.removeAttribute(attr.name);
    }
  }
  return doc.innerHTML;
}

/** Persist both the new page ID and retry key when a create-like action loses its response. */
export class NoteActions {
  private send: typeof api;
  private storage: Pick<Storage, "getItem" | "setItem" | "removeItem">;
  constructor(
    storage: Pick<Storage, "getItem" | "setItem" | "removeItem">,
    send: typeof api = api,
  ) {
    this.storage = storage;
    this.send = send;
  }
  async create(
    uid: string,
    scope: string,
    request: (id: string) => { path: string; body: unknown },
  ) {
    const storageKey = `ca-note-action:${uid}:${scope}`;
    const raw = this.storage.getItem(storageKey);
    const saved = raw
      ? (JSON.parse(raw) as { key: string; path: string; body: unknown })
      : { key: uuidv7(), ...request(uuidv7()) };
    this.storage.setItem(storageKey, JSON.stringify(saved));
    try {
      const note = Note.parse(
        await this.send(saved.path, {
          method: "POST",
          ...(scope.startsWith("blog:") ? { method: "PUT" } : {}),
          body: saved.body,
          key: saved.key,
          accountId: uid,
        }),
      );
      this.storage.removeItem(storageKey);
      return note;
    } catch (error) {
      if (error instanceof HttpError && error.status < 500 && error.status !== 429)
        this.storage.removeItem(storageKey);
      throw error;
    }
  }
}
